/**
 * DaVinci Booking Service
 *
 * High-level booking orchestration that handles the complete booking flow:
 * 1. Convert BA response to booking request format
 * 2. Enrich with client data from checkout form
 * 3. Execute appropriate booking action (B, BQ, BC) based on availability code
 * 4. Return booking result with number or error
 *
 * Booking flow by availability code:
 * - 712 (Fixed booking) → B action → expects return code 200
 * - 733 (Request booking) → BQ action → expects return code 206
 * - 923 (Confirmation required) → BC action → expects return code 200
 */

import { convertResponse } from './convertResponse';
import {
  performFixedBooking,
  performBookingQuery,
  performBookingConfirmation,
} from './performAction';
import {
  createClientFromFormData,
  createParticipantsFromTravelers,
  createDeliveryConfig,
  createPaymentConfig,
} from './helpers';
import {
  getActionForCode,
  isAvailableCode,
  BOOKING_SUCCESS_CODES,
} from '@/constants/davinci/returnCodes';
import type { BookingSubmitResult } from '@/types/checkout';
import type { CheckoutFormData } from '@/types/schemas/checkout';
import type { Traveler } from '@/types/Traveler';
import { DaVinciApiError, DaVinciNetworkError } from '@/types/errors';

/**
 * Parameters for booking submission
 */
export interface BookingSubmitParams {
  /** Session ID from BA response */
  sessionId: string;
  /** Activity ID from BA response */
  activityId: string;
  /** Booking object from BA response */
  bookingResponse: Record<string, unknown>;
  /** Availability code from BA response (712, 733, or 923) */
  availabilityCode: number;
  /** Checkout form data with contact person and travelers */
  formData: CheckoutFormData;
  /** Travelers summary (includes contact person if traveling) */
  travelers: Traveler[];
  /** Payment type code (default: 8 for bank transfer) */
  paymentType?: number;
  /**
   * TravelType captured from the first BA response. When provided, forced onto the
   * converted booking so B/BQ/BC use the same value as the BA flow.
   */
  travelType?: string;
  /**
   * AgencyNumber derived from the captured TravelType. When provided, forced onto
   * the converted booking (the API's echoed AgencyNumber is not reliable).
   */
  agencyNumber?: number;
}

/**
 * Extract booking number from DaVinci response
 *
 * @param response - PerformAction response
 * @returns Booking number or undefined if not found
 */
function extractBookingNumber(response: Record<string, unknown>): string | undefined {
  const booking = response.Booking as Record<string, unknown> | undefined;
  if (!booking) return undefined;

  const bookingNumber = booking.BookingNumber;
  if (typeof bookingNumber === 'string') return bookingNumber;
  if (typeof bookingNumber === 'number') return String(bookingNumber);

  return undefined;
}

/**
 * Extract return code from DaVinci response
 *
 * @param response - PerformAction response
 * @returns Return code or 999 if not found
 */
function extractReturnCode(response: Record<string, unknown>): number {
  const booking = response.Booking as Record<string, unknown> | undefined;
  if (!booking) return 999;

  const returnCode = booking.ReturnCode;
  if (typeof returnCode === 'number') return returnCode;

  return 999;
}

/**
 * Extract return message from DaVinci response
 *
 * @param response - PerformAction response
 * @returns Return message or undefined if not found
 */
function extractReturnMessage(response: Record<string, unknown>): string | undefined {
  const booking = response.Booking as Record<string, unknown> | undefined;
  if (!booking) return undefined;

  const description = booking.ReturnCodeDescription;
  if (typeof description === 'string') return description;

  return undefined;
}

/**
 * Check if return code indicates success for the given action
 *
 * @param returnCode - Return code from DaVinci
 * @param actionType - Action type ('B', 'BQ', or 'BC')
 * @returns true if the return code indicates success
 */
function isSuccessCode(returnCode: number, actionType: 'B' | 'BQ' | 'BC'): boolean {
  if (actionType === 'BQ') {
    return BOOKING_SUCCESS_CODES.REQUEST_BOOKING.includes(returnCode);
  }
  if (actionType === 'BC') {
    return BOOKING_SUCCESS_CODES.CONFIRMATION.includes(returnCode);
  }
  return BOOKING_SUCCESS_CODES.FIXED_BOOKING.includes(returnCode);
}

/**
 * Submit booking to DaVinci API
 *
 * Orchestrates the complete booking flow:
 * 1. Convert BA response using ConvertResponseToNewRequest
 * 2. Enrich booking with real client/participant data
 * 3. Execute appropriate action (B/BQ/BC) based on availability code
 * 4. Return result with booking number or error
 *
 * @param params - Booking submission parameters
 * @returns Booking result with success status and booking number
 *
 * @example
 * const result = await submitBooking({
 *   sessionId: 'session-id-from-ba',
 *   activityId: 'activity-id-from-ba',
 *   bookingResponse: baResponse.Booking,
 *   availabilityCode: 712,
 *   formData: checkoutFormData,
 *   travelers: travelersSummary
 * });
 *
 * if (result.success) {
 *   console.log('Booking number:', result.bookingNumber);
 * } else {
 *   console.error('Booking failed:', result.error);
 * }
 */
export async function submitBooking(params: BookingSubmitParams): Promise<BookingSubmitResult> {
  const {
    sessionId,
    activityId,
    bookingResponse,
    availabilityCode,
    formData,
    travelers,
    paymentType,
    travelType,
    agencyNumber,
  } = params;

  const actionType = getActionForCode(availabilityCode);
  if (!actionType) {
    return {
      success: false,
      returnCode: availabilityCode,
      error: `Unsupported availability code: ${availabilityCode}`,
    };
  }

  try {
    // Step 1: Convert BA response to request format
    const convertedResponse = await convertResponse(sessionId, activityId, bookingResponse, {});

    const newActivityId = convertedResponse.ActivityId;
    const convertedBooking = convertedResponse.Booking as Record<string, unknown>;

    if (!convertedBooking) {
      return {
        success: false,
        returnCode: 999,
        error: 'Convert response did not return booking object',
      };
    }

    // Step 2: Enrich booking with real client and participant data
    const enrichedBooking: Record<string, unknown> = {
      ...convertedBooking,
      ...(travelType !== undefined ? { TravelType: travelType } : {}),
      ...(agencyNumber !== undefined ? { AgencyNumber: agencyNumber } : {}),
      Client: createClientFromFormData(formData),
      Participants: createParticipantsFromTravelers(travelers),
    };

    // Step 3: Execute appropriate booking action
    let response: Record<string, unknown>;
    const delivery = createDeliveryConfig();
    const payment = createPaymentConfig(paymentType);

    switch (actionType) {
      case 'B':
        response = (await performFixedBooking(
          sessionId,
          newActivityId,
          enrichedBooking,
          {},
          delivery,
          payment
        )) as unknown as Record<string, unknown>;
        break;

      case 'BQ':
        response = (await performBookingQuery(
          sessionId,
          newActivityId,
          enrichedBooking,
          {},
          undefined,
          undefined
        )) as unknown as Record<string, unknown>;
        break;

      case 'BC':
        response = (await performBookingConfirmation(
          sessionId,
          newActivityId,
          enrichedBooking,
          {},
          delivery,
          payment
        )) as unknown as Record<string, unknown>;
        break;
    }

    // Step 4: Extract result
    const returnCode = extractReturnCode(response);
    const bookingNumber = extractBookingNumber(response);
    const message = extractReturnMessage(response);
    const success = isSuccessCode(returnCode, actionType);

    if (success && bookingNumber) {
      return {
        success: true,
        bookingNumber,
        returnCode,
        message,
      };
    }

    // Flow change: API responded with an available BA code that maps to a different action
    const returnedAction = getActionForCode(returnCode);
    if (
      isAvailableCode(returnCode) &&
      returnedAction !== undefined &&
      returnedAction !== actionType
    ) {
      console.warn(
        `[BookingService] Flow change: ${actionType} → ${returnedAction} (code ${returnCode})`
      );
      return {
        success: false,
        returnCode,
        message,
        flowChanged: true,
        newAvailabilityCode: returnCode,
      };
    }

    console.warn(`[BookingService] Booking failed. Code: ${returnCode}, Message: ${message}`);
    return {
      success: false,
      returnCode,
      message,
      error: message || `Booking failed with code ${returnCode}`,
    };
  } catch (error) {
    console.error('[BookingService] Error during booking:', error);

    if (error instanceof DaVinciNetworkError) {
      return {
        success: false,
        returnCode: 999,
        error: error.isTimeout
          ? 'Booking request timed out. Please try again.'
          : 'Network error during booking. Please check your connection.',
      };
    }

    if (error instanceof DaVinciApiError) {
      const errorCode = error.code ? parseInt(error.code, 10) : 999;
      return {
        success: false,
        returnCode: isNaN(errorCode) ? 999 : errorCode,
        error: error.message || 'API error during booking',
      };
    }

    return {
      success: false,
      returnCode: 999,
      error: error instanceof Error ? error.message : 'Unknown error during booking',
    };
  }
}
